import { useCallback, useEffect, useState } from 'react';
import { logger } from '@/utils/logger';

export enum MicrophonePermissionState {
  CHECKING = 'checking',
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
  ERROR = 'error',
}

export interface UseMicrophonePermissionReturn {
  permissionState: MicrophonePermissionState;
  requestPermission: () => Promise<boolean>;
  checkPermission: () => Promise<MicrophonePermissionState>;
  error?: Error;
}

/**
 * Hook for checking and requesting microphone permission
 *
 * @returns {UseMicrophonePermissionReturn} Object containing permission state and methods
 *
 * @example
 * ```tsx
 * const { permissionState, requestPermission } = useMicrophonePermission();
 *
 * if (permissionState === MicrophonePermissionState.DENIED) {
 *   // Show error UI
 * }
 *
 * // Request permission
 * const granted = await requestPermission();
 * ```
 */
export function useMicrophonePermission(): UseMicrophonePermissionReturn {
  const [permissionState, setPermissionState] = useState<MicrophonePermissionState>(MicrophonePermissionState.CHECKING);
  const [error, setError] = useState<Error>();

  const checkPermission = useCallback(async (): Promise<MicrophonePermissionState> => {
    try {
      // Use Permissions API if supported
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

        let state: MicrophonePermissionState;
        switch (permissionStatus.state) {
          case 'granted':
            state = MicrophonePermissionState.GRANTED;
            break;
          case 'denied':
            state = MicrophonePermissionState.DENIED;
            break;
          case 'prompt':
            state = MicrophonePermissionState.PROMPT;
            break;
          default:
            state = MicrophonePermissionState.PROMPT;
        }

        setPermissionState(state);
        return state;
      }
      // Fallback: when Permissions API is not supported, we cannot check without prompting
      // Return PROMPT state and let requestPermission handle the actual permission request
      setPermissionState(MicrophonePermissionState.PROMPT);
      return MicrophonePermissionState.PROMPT;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to check microphone permission');
      setError(errorObj);
      setPermissionState(MicrophonePermissionState.ERROR);
      return MicrophonePermissionState.ERROR;
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setPermissionState(MicrophonePermissionState.CHECKING);

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop the stream immediately as we only need to check permission
      stream.getTracks().forEach((track) => track.stop());

      setPermissionState(MicrophonePermissionState.GRANTED);
      setError(undefined);
      return true;
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setPermissionState(MicrophonePermissionState.DENIED);
          setError(new Error('Microphone permission denied by user'));
        } else if (err.name === 'NotFoundError') {
          setPermissionState(MicrophonePermissionState.ERROR);
          setError(new Error('No microphone device found'));
        } else {
          setPermissionState(MicrophonePermissionState.ERROR);
          setError(err);
        }
      } else {
        const errorObj = new Error('Failed to request microphone permission');
        setError(errorObj);
        setPermissionState(MicrophonePermissionState.ERROR);
      }
      return false;
    }
  }, []);

  // Check permission on mount. Deferred to a microtask so the (async) state updates it triggers
  // are not treated as synchronous setState-in-effect; the permission query itself is async.
  // The cancelled flag guards Strict Mode (mount → unmount → remount) and regular unmount so the
  // queued check never runs after cleanup, which would otherwise trigger a stale state update.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) {
        return;
      }
      void checkPermission();
    });
    return () => {
      cancelled = true;
    };
  }, [checkPermission]);

  // Listen for permission changes (if Permissions API is supported)
  useEffect(() => {
    if (!navigator.permissions) {
      return;
    }

    let permissionStatus: PermissionStatus | undefined;

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        permissionStatus.addEventListener('change', checkPermission);
      })
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(`${err}`);
        logger.error('Failed to setup microphone permission change listener', error, {
          errorName: error.name,
          errorMessage: error.message,
        });
      });

    return () => {
      permissionStatus?.removeEventListener('change', checkPermission);
    };
  }, [checkPermission]);

  return {
    permissionState,
    requestPermission,
    checkPermission,
    error,
  };
}
